import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { Menu } from "./Menu";
import { useEffect, useMemo, useState } from "react";
import { Connection } from "../sockets-client";
import type { ActiveRoomInfo } from "../context/UserContext";
import { SESSION_APPBAR_TOOLBAR_PX } from "./SessionAppBar";

type Props = {
  playerName: string;
  socketConnection: Connection;
  hostedRoomId?: string | null;
  namespacePath?: string | null;
  onBack?: () => void;
  activeRooms?: ActiveRoomInfo[];
  onResumeRoom?: (room: ActiveRoomInfo) => void;
  onRefreshResume?: () => void | Promise<void>;
};

export const RoomSelection = ({
  playerName,
  socketConnection,
  hostedRoomId,
  namespacePath,
  onBack,
  activeRooms = [],
  onResumeRoom,
  onRefreshResume,
}: Props) => {
  const isLiterature = namespacePath === "/literature";
  const isRummy = namespacePath === "/rummy";
  const dropDownOptions = isLiterature
    ? [{ value: "6", name: "6 (required)" }]
    : isRummy
      ? [
          { value: "2", name: "2" },
          { value: "3", name: "3" },
          { value: "4", name: "4" },
          { value: "5", name: "5" },
          { value: "6", name: "6" },
        ]
      : [
          { value: "4", name: "4" },
          { value: "6", name: "6" },
          { value: "8", name: "8" },
          { value: "12", name: "12" },
        ];

  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const onClickCreateRoom = () => setCreateOpen(true);
  const onClickJoinByCode = () => setJoinOpen(true);

  const gameTitle = isLiterature ? "Literature Rooms" : isRummy ? "Rummy Rooms" : "Addata Rooms";
  const resumeRooms = useMemo(
    () => activeRooms.filter((r) => !namespacePath || r.namespace === namespacePath),
    [activeRooms, namespacePath]
  );

  useEffect(() => {
    if (!onRefreshResume) return;
    void onRefreshResume();
  }, [onRefreshResume]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: {
          xs: `calc(100vh - ${SESSION_APPBAR_TOOLBAR_PX.xs}px)`,
          sm: `calc(100vh - ${SESSION_APPBAR_TOOLBAR_PX.sm}px)`,
        },
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1.25, sm: 1.5 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1080,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
            px: { xs: 0.5, sm: 1 },
          }}
        >
          <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: 0.75 }}>
            {onBack ? (
              <IconButton
                onClick={onBack}
                aria-label="Back to games"
                size="small"
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  mt: 0.2,
                  color: "rgba(255,255,255,0.9)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backgroundColor: "rgba(255,255,255,0.04)",
                }}
              >
                <ArrowBackRoundedIcon fontSize="small" />
              </IconButton>
            ) : null}
            <Box sx={{ minWidth: 0 }}>
            <Typography color="white" variant="h4" sx={{ fontSize: { xs: "1.55rem", sm: "2rem" }, fontWeight: 700, lineHeight: 1.1 }}>
              {gameTitle}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: { xs: "0.85rem", sm: "1rem" } }}>
              Join an existing room or create your own
            </Typography>
            </Box>
          </Box>
          <Button
            onClick={onClickCreateRoom}
            variant="contained"
            color="success"
            sx={{
              minWidth: { xs: 128, sm: 168 },
              minHeight: 40,
              fontWeight: 700,
              whiteSpace: "nowrap",
              boxShadow: "0 0 0 2px rgba(76,175,80,0.35)",
            }}
          >
            + Create Room
          </Button>
        </Box>
        <Stack spacing={1.2}>
          {onResumeRoom && resumeRooms.length > 0 ? (
            <>
              {resumeRooms.map((room) => (
                <Paper
                  key={`${room.namespace}-${room.roomId}`}
                  elevation={4}
                  sx={{
                    width: "100%",
                    borderRadius: 2,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "linear-gradient(160deg, rgba(30,30,32,0.96) 0%, rgba(20,20,22,0.92) 100%)",
                    p: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography color="white" sx={{ fontSize: { xs: "1.2rem", sm: "1.45rem" }, fontWeight: 700 }}>
                      Room {room.roomId}
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.35,
                        borderRadius: 1,
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: room.phase === "playing" ? "#81c784" : "#a5d6a7",
                        backgroundColor: "rgba(76,175,80,0.15)",
                      }}
                    >
                      {room.phase === "playing" ? "In progress" : "Waiting"}
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => onResumeRoom(room)}
                    sx={{
                      mt: 0.8,
                      minHeight: 44,
                      fontWeight: 700,
                      backgroundColor: "rgba(0,0,0,0.72)",
                      border: "1px solid rgba(255,255,255,0.14)",
                    }}
                  >
                    Join Room
                  </Button>
                </Paper>
              ))}
              <Button
                variant="text"
                onClick={onClickJoinByCode}
                sx={{ color: "rgba(255,255,255,0.72)", alignSelf: "flex-start", textTransform: "none" }}
              >
                Join by room code
              </Button>
            </>
          ) : (
            <Paper
              elevation={8}
              sx={{
                width: "100%",
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "linear-gradient(160deg, rgba(30,30,32,0.96) 0%, rgba(20,20,22,0.92) 100%)",
                p: { xs: 1.5, sm: 2 },
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: { xs: "0.88rem", sm: "0.95rem" }, mb: 1.2 }}>
                No active rooms available right now.
              </Typography>
              <Button variant="outlined" onClick={onClickJoinByCode} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.24)" }}>
                Join by Room ID
              </Button>
            </Paper>
          )}
        </Stack>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ pb: 1 }}>Create New Room</DialogTitle>
          <DialogContent>
            <Menu
              label="Number of players"
              options={dropDownOptions}
              isInputTextField={true}
              socketConnection={socketConnection}
              hostedRoomId={hostedRoomId}
              namespacePath={namespacePath}
              hideBots={isLiterature || isRummy}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={joinOpen} onClose={() => setJoinOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ pb: 1 }}>Join Room</DialogTitle>
          <DialogContent>
            <Menu
              label="Room ID"
              options={dropDownOptions}
              isInputTextField={false}
              socketConnection={socketConnection}
              hostedRoomId={null}
              namespacePath={namespacePath}
              hideBots={isLiterature || isRummy}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};
